/**
 * ScopedListManager — one Brand Knowledge list field (cas d'usage, sujets d'autorité,
 * critères, questions, objections, sujets à éviter, différenciateurs), built to hold
 * hundreds of items, each optionally scoped to ONE Cibles target.
 *
 * Rows, not chips: a toolbar (search · target filter · item count), a composer that takes
 * Enter or a multi-line paste, then one light memoised row per item (inline edit, scope
 * pill, delete on hover) inside an internally scrolled pane. `full` adds multi-select
 * and bulk « Assigner à… » / « Supprimer ».
 *
 * Contract with the parent (BrandStrategy.tsx):
 *  - controlled and immutable: every change is a NEW array / object through
 *    `onItemsChange` / `onScopesChange`; an operation touching both calls
 *    `onItemsChange` FIRST, then `onScopesChange`, in the same handler, so the parent
 *    must apply them with functional state updates;
 *  - `scopes` is keyed by EXACT item text, so a rename re-keys its entry and a delete
 *    removes it. All list logic lives in `scopedList.ts` (unit tested).
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase, IconButton, InputBase, Menu, MenuItem, Skeleton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { StrategyScopedList } from '@traaker/shared';
import { targetColorHex } from '../../../utils/targetColors';
import {
  SCOPE_FILTER_ALL,
  SCOPE_FILTER_GLOBAL,
  appendItems,
  assignScope,
  effectiveScopes,
  filterByScope,
  parsePastedItems,
  removeItems,
  renameItem,
  scopeCounts,
  type PasteResult,
  type ScopeFilter,
  type Scopes,
} from './scopedList';
import { Highlight } from './Highlight';
import { TargetScopePicker } from './TargetScopePicker';
import { K, markSx, microLabel } from './tokens';
import type { StrategyTargets } from './useStrategyTargets';

export interface ScopedListManagerProps {
  field: StrategyScopedList;
  items: string[];
  onItemsChange: (items: string[]) => void;
  scopes: Record<string, string>;                 // itemText -> targetId (this field's slice of itemTargets)
  onScopesChange: (scopes: Record<string, string>) => void;
  limits: { maxItems: number; maxCharsPerItem: number };
  targets: StrategyTargets;
  /** The stored scopes are not loaded yet / failed to load: the scope column shows a placeholder and is not editable. */
  scopesUnavailable?: 'loading' | 'error';
  readOnly: boolean;
  placeholder?: string;
  error?: string;            // server guardrail message for this field
  emptyHint?: string;        // shown when no items
  variant?: 'full' | 'compact';  // full = primary lists (large pane), compact = secondary sections
}

/** Target pills shown inline in the filter before the « +N » overflow menu. */
const INLINE_TARGET_PILLS = 4;
/** Below this many items the compact variant has no search field. */
const COMPACT_SEARCH_FROM = 8;

// ─────────────────────────────────────────────────────────────────────────────
// Small presentational bits
// ─────────────────────────────────────────────────────────────────────────────

function SegButton({ on, onClick, children, dot }: { on: boolean; onClick: (e: React.MouseEvent<HTMLElement>) => void; children: React.ReactNode; dot?: string }) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={on}
      sx={{
        gap: 0.75, fontSize: 12, fontWeight: 600, px: 1.25, py: '5px', borderRadius: '8px',
        whiteSpace: 'nowrap', color: on ? K.ink : K.ink2, maxWidth: 200,
        bgcolor: on ? K.paper : 'transparent', boxShadow: on ? '0 1px 2px rgba(0,0,0,.06)' : 'none',
        '&:hover': { color: K.ink },
        '&.Mui-focusVisible': { boxShadow: K.focusRing },
      }}
    >
      {dot && <Box component="i" sx={{ width: 6, height: 6, borderRadius: '999px', bgcolor: dot, flex: '0 0 6px', opacity: on ? 1 : 0.55 }} />}
      {children}
    </ButtonBase>
  );
}

function Count({ n }: { n: number }) {
  return <Box component="span" sx={{ color: K.ink3, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{n}</Box>;
}

function Check({ on, mixed, onClick, label, visible }: { on: boolean; mixed?: boolean; onClick: () => void; label: string; visible: boolean }) {
  return (
    <ButtonBase
      role="checkbox"
      aria-checked={mixed ? 'mixed' : on}
      aria-label={label}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="sl-check"
      sx={{
        flex: '0 0 14px', width: 14, height: 14, borderRadius: '4px',
        border: `1.5px solid ${on || mixed ? K.primary : '#D4D4D8'}`, bgcolor: on || mixed ? K.primary : K.paper,
        opacity: visible || on ? 1 : 0, transition: 'opacity .12s',
        '&.Mui-focusVisible': { opacity: 1, boxShadow: K.focusRing },
        '&::after': on
          ? { content: '""', width: 3.5, height: 7.5, mt: '-2px', border: 'solid #fff', borderWidth: '0 1.8px 1.8px 0', transform: 'rotate(45deg)' }
          : mixed ? { content: '""', width: 7, height: 1.8, bgcolor: '#fff', borderRadius: 1 } : {},
      }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Row
// ─────────────────────────────────────────────────────────────────────────────

interface RowActions {
  toggle: (text: string) => void;
  startEdit: (text: string) => void;
  setEditValue: (v: string) => void;
  commitEdit: () => void;
  cancelEdit: () => void;
  remove: (text: string) => void;
  setScope: (text: string, targetId: string | undefined) => void;
}

interface RowProps {
  text: string;
  scopeId?: string;
  selected: boolean;
  selectable: boolean;
  selecting: boolean;
  readOnly: boolean;
  showScope: boolean;
  scopesUnavailable?: 'loading' | 'error';
  dense: boolean;
  query: string;
  editing: boolean;
  editValue: string;
  editError: string | null;
  maxChars: number;
  targets: StrategyTargets;
  actions: RowActions;
}

const Row = memo(function Row({
  text, scopeId, selected, selectable, selecting, readOnly, showScope, scopesUnavailable, dense, query,
  editing, editValue, editError, maxChars, targets, actions,
}: RowProps) {
  const { t } = useTranslation();
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); actions.commitEdit(); }
    else if (e.key === 'Escape') { e.preventDefault(); actions.cancelEdit(); }
  };
  return (
    <Box
      role="listitem"
      sx={{
        display: 'flex', alignItems: editing ? 'flex-start' : 'center', gap: 1.25,
        px: dense ? 1.5 : 2, py: dense ? '4px' : '6px', minHeight: dense ? 34 : 40,
        borderBottom: `1px solid ${K.line}`, bgcolor: selected ? K.primarySoft : editing ? K.rowHover : K.paper,
        '&:last-of-type': { borderBottom: 0 },
        '&:hover': { bgcolor: selected ? K.primarySoft : K.rowHover },
        '&:hover .sl-check, &:focus-within .sl-check': { opacity: 1 },
        '&:hover .sl-del, &:focus-within .sl-del': { opacity: 1 },
      }}
    >
      {selectable && (
        <Box sx={{ display: 'flex', alignSelf: 'center' }}>
          <Check on={selected} onClick={() => actions.toggle(text)} label={t('brandKnowledge.list.selectItem')} visible={selecting} />
        </Box>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <>
            <InputBase
              autoFocus
              fullWidth
              multiline
              value={editValue}
              onChange={(e) => actions.setEditValue(e.target.value.replace(/\n/g, ' '))}
              onKeyDown={onKey}
              onBlur={actions.commitEdit}
              inputProps={{ maxLength: maxChars, 'aria-label': text }}
              sx={{
                fontSize: dense ? 13 : 13.5, lineHeight: '19px', fontWeight: 500, color: K.ink,
                border: `1px solid ${editError ? K.error : K.primary}`, borderRadius: '8px', px: 1, py: '4px',
                bgcolor: K.paper, boxShadow: K.focusRing,
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 0.4, fontSize: 11, color: K.ink3 }}>
              {editError && <Box component="span" sx={{ color: K.errorInk, fontWeight: 600 }}>{editError}</Box>}
              <Box component="span" sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>{editValue.length} / {maxChars}</Box>
            </Box>
          </>
        ) : (
          <Box
            title={readOnly ? text : `${text}\n${t('brandKnowledge.list.editHint')}`}
            onClick={readOnly ? undefined : () => actions.startEdit(text)}
            onKeyDown={readOnly ? undefined : (e) => { if (e.key === 'Enter' || e.key === 'F2') { e.preventDefault(); actions.startEdit(text); } }}
            tabIndex={readOnly ? undefined : 0}
            sx={{
              fontSize: dense ? 13 : 13.5, lineHeight: '20px', fontWeight: 500, color: K.ink, letterSpacing: '-0.006em',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              cursor: readOnly ? 'default' : 'text', borderRadius: '6px', outline: 'none',
              '&:focus-visible': { boxShadow: K.focusRing },
              ...markSx,
            }}
          >
            <Highlight text={text} query={query} />
          </Box>
        )}
      </Box>

      {showScope && (
        <Box sx={{ flex: dense ? '0 0 160px' : '0 0 196px', minWidth: 0, display: 'flex', justifyContent: 'flex-start', alignSelf: 'center' }}>
          {scopesUnavailable === 'loading' ? (
            <Skeleton variant="rounded" width={dense ? 88 : 104} height={18} aria-label={t('brandKnowledge.scope.loading')} />
          ) : scopesUnavailable === 'error' ? (
            <Box component="span" sx={{ fontSize: 12, color: K.ink3 }}>{t('brandKnowledge.scope.unavailable')}</Box>
          ) : (
            <TargetScopePicker
              value={scopeId}
              onChange={(id) => actions.setScope(text, id)}
              targets={targets}
              disabled={readOnly}
            />
          )}
        </Box>
      )}

      {!readOnly && (
        <IconButton
          className="sl-del"
          size="small"
          aria-label={t('brandKnowledge.list.delete')}
          onClick={() => actions.remove(text)}
          sx={{
            alignSelf: 'center', p: '3px', opacity: 0, color: K.ink3, transition: 'opacity .12s',
            '&:hover': { color: K.error, bgcolor: 'rgba(239,68,68,.08)' },
            '&.Mui-focusVisible': { opacity: 1 },
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// Manager
// ─────────────────────────────────────────────────────────────────────────────

export function ScopedListManager({
  field,
  items,
  onItemsChange,
  scopes,
  onScopesChange,
  limits,
  targets,
  scopesUnavailable,
  readOnly,
  placeholder,
  error,
  emptyHint,
  variant = 'full',
}: ScopedListManagerProps) {
  const { t } = useTranslation();
  const full = variant === 'full';
  const dense = !full;

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ScopeFilter>(SCOPE_FILTER_ALL);
  const [draft, setDraft] = useState('');
  const [feedback, setFeedback] = useState<PasteResult | null>(null);
  const [selection, setSelection] = useState<Set<string>>(() => new Set());
  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null);

  // Reset view state when the parent swaps to another field.
  useEffect(() => {
    setQuery(''); setFilter(SCOPE_FILTER_ALL); setDraft(''); setFeedback(null);
    setSelection(new Set()); setEditing(null); editRef.current = null;
  }, [field]);

  // Drop selected texts that no longer exist (deleted, renamed elsewhere).
  useEffect(() => {
    setSelection((prev) => {
      if (!prev.size) return prev;
      const present = new Set(items);
      const next = new Set([...prev].filter((s) => present.has(s)));
      return next.size === prev.size ? prev : next;
    });
  }, [items]);

  // While the targets are loading, a not-yet-resolved id is treated as resolvable so the
  // list does not flash every scoped item into « Globales ».
  const isResolvable = useCallback(
    (id: string) => !!targets.resolve(id) || (targets.loading && !targets.missingIds.has(id)),
    [targets],
  );
  const eff = useMemo(() => effectiveScopes(scopes, isResolvable), [scopes, isResolvable]);
  const counts = useMemo(() => scopeCounts(items, eff), [items, eff]);
  const visible = useMemo(() => filterByScope(items, eff, filter, query), [items, eff, filter, query]);

  const filterTarget = filter !== SCOPE_FILTER_ALL && filter !== SCOPE_FILTER_GLOBAL ? filter : undefined;
  const targetLabel = (id: string) => targets.resolve(id)?.label ?? t('brandKnowledge.scope.loading');

  const showScope = !!scopesUnavailable || targets.loading || targets.options.length > 0 || Object.keys(eff).length > 0 || targets.error;
  const showFilter = counts.byTarget.length > 0 || filter !== SCOPE_FILTER_ALL;
  const showSearch = full || items.length >= COMPACT_SEARCH_FROM || !!query;
  const selectable = full && !readOnly;
  const atMax = items.length >= limits.maxItems;

  // Inline target pills: top N by count, plus the active one if it sits in the overflow.
  const inlineTargets = counts.byTarget.slice(0, INLINE_TARGET_PILLS);
  if (filterTarget && !inlineTargets.some(([id]) => id === filterTarget)) {
    inlineTargets.push([filterTarget, counts.byTarget.find(([id]) => id === filterTarget)?.[1] ?? 0]);
  }
  const overflowTargets = counts.byTarget.filter(([id]) => !inlineTargets.some(([x]) => x === id));

  // ── Latest props for stable row callbacks ──
  const live = useRef({ items, scopes, onItemsChange, onScopesChange, limits, editValue });
  live.current = { items, scopes, onItemsChange, onScopesChange, limits, editValue };
  // The text being edited, set SYNCHRONOUSLY: the blur that follows Enter / Escape must
  // not commit a second time (or commit an edit the user just cancelled).
  const editRef = useRef<string | null>(null);
  // `t` changes identity on language switch; the stable row actions read it through a ref.
  const tRef = useRef(t);
  tRef.current = t;

  const actions = useMemo<RowActions>(() => ({
    toggle: (text) => setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(text)) next.delete(text); else next.add(text);
      return next;
    }),
    startEdit: (text) => { editRef.current = text; setEditing(text); setEditValue(text); setEditError(null); },
    setEditValue: (v) => { setEditValue(v); setEditError(null); },
    cancelEdit: () => { editRef.current = null; setEditing(null); setEditError(null); },
    commitEdit: () => {
      const from = editRef.current;
      if (from === null) return;
      const { items: its, scopes: sc, editValue: to, limits: lim } = live.current;
      const res = renameItem(its, sc, from, to, lim.maxCharsPerItem);
      if (!res.ok) {
        if (res.reason === 'empty') { editRef.current = null; setEditing(null); return; }
        setEditError(res.reason === 'duplicate'
          ? tRef.current('brandKnowledge.list.renameDuplicate')
          : tRef.current('brandKnowledge.list.renameTooLong', { max: lim.maxCharsPerItem }));
        return;
      }
      editRef.current = null;
      setEditing(null);
      if (!res.changed) return;
      live.current.onItemsChange(res.items);
      if (from in sc) live.current.onScopesChange(res.scopes);
      setSelection((prev) => (prev.has(from) ? new Set([...prev].filter((s) => s !== from)) : prev));
    },
    remove: (text) => {
      const { items: its, scopes: sc } = live.current;
      const res = removeItems(its, sc, [text]);
      live.current.onItemsChange(res.items);
      if (text in sc) live.current.onScopesChange(res.scopes);
    },
    setScope: (text, targetId) => {
      const sc = live.current.scopes;
      if ((sc[text] ?? undefined) === targetId) return;
      live.current.onScopesChange(assignScope(sc, [text], targetId));
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // ── Add ──
  const add = (raw: string) => {
    const res = parsePastedItems(raw, items, limits);
    const multi = /\r|\n/.test(raw.trim());
    if (res.added.length) {
      const next = appendItems(items, scopes, res.added, filterTarget);
      onItemsChange(next.items);
      if (filterTarget) onScopesChange(next.scopes);
      setDraft('');
      setQuery('');
    }
    const skipped = res.skippedDuplicate + res.skippedTooLong + res.skippedOverLimit;
    setFeedback(multi || skipped ? res : null);
  };

  const onDraftKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (draft.trim()) add(draft); }
  };
  const onDraftPaste = (e: ClipboardEvent) => {
    const text = e.clipboardData.getData('text');
    if (!/\r|\n/.test(text.trim())) return; // single line: let the input take it
    e.preventDefault();
    add(draft.trim() ? `${draft}\n${text}` : text);
  };

  // ── Bulk ──
  const selectedVisible = visible.filter((v) => selection.has(v));
  const allVisibleSelected = visible.length > 0 && selectedVisible.length === visible.length;
  const toggleAllVisible = () => setSelection((prev) => {
    const next = new Set(prev);
    if (allVisibleSelected) for (const v of visible) next.delete(v);
    else for (const v of visible) next.add(v);
    return next;
  });
  const bulkAssign = (targetId: string | undefined) => {
    onScopesChange(assignScope(scopes, selection, targetId));
    setSelection(new Set());
  };
  const bulkDelete = () => {
    const res = removeItems(items, scopes, selection);
    onItemsChange(res.items);
    if ([...selection].some((s) => s in scopes)) onScopesChange(res.scopes);
    setSelection(new Set());
  };

  const resetFilters = () => { setQuery(''); setFilter(SCOPE_FILTER_ALL); };
  const filtered = !!query.trim() || filter !== SCOPE_FILTER_ALL;

  // ── Feedback line ──
  const feedbackLine = feedback && (
    <Box
      role="status"
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', fontSize: 12, color: K.ink2,
        px: 1.25, py: 0.6, borderRadius: '8px',
        bgcolor: feedback.added.length ? 'rgba(16,185,129,.08)' : 'rgba(245,158,11,.10)',
      }}
    >
      <Box component="span" sx={{ fontWeight: 600, color: feedback.added.length ? '#047857' : K.warningInk }}>
        {feedback.added.length
          ? t('brandKnowledge.list.pasteAdded', { count: feedback.added.length })
          : t('brandKnowledge.list.nothingAdded')}
      </Box>
      {feedback.skippedDuplicate > 0 && <span>· {t('brandKnowledge.list.pasteSkippedDuplicate', { count: feedback.skippedDuplicate })}</span>}
      {feedback.skippedTooLong > 0 && <span>· {t('brandKnowledge.list.pasteSkippedTooLong', { count: feedback.skippedTooLong, max: limits.maxCharsPerItem })}</span>}
      {feedback.skippedOverLimit > 0 && <span>· {t('brandKnowledge.list.pasteSkippedOverLimit', { count: feedback.skippedOverLimit, limit: limits.maxItems })}</span>}
      <IconButton size="small" aria-label={t('brandKnowledge.list.dismiss')} onClick={() => setFeedback(null)} sx={{ ml: 'auto', p: '2px', color: K.ink3 }}>
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-field={field}>
      {/* ── Toolbar: search · target filter · item count ── */}
      {(showSearch || showFilter || full) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          {showSearch && (
            <Box
              sx={{
                position: 'relative', display: 'flex', alignItems: 'center', width: full ? 220 : 180, flex: '0 0 auto',
                border: `1px solid ${K.line}`, borderRadius: '10px', bgcolor: K.paper,
                '&:focus-within': { borderColor: K.primary, boxShadow: K.focusRing },
              }}
            >
              <SearchIcon sx={{ fontSize: 16, color: K.ink3, position: 'absolute', left: 9, pointerEvents: 'none' }} />
              <InputBase
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') setQuery(''); }}
                placeholder={t('brandKnowledge.list.searchPlaceholder')}
                inputProps={{ 'aria-label': t('brandKnowledge.list.searchAria') }}
                sx={{ flex: 1, fontSize: 12.5, pl: '29px', pr: query ? '24px' : 1, py: '3px' }}
              />
              {query && (
                <IconButton size="small" aria-label={t('brandKnowledge.list.clearSearch')} onClick={() => setQuery('')} sx={{ position: 'absolute', right: 2, p: '3px', color: K.ink3 }}>
                  <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
              )}
            </Box>
          )}

          {showFilter && (
            <Box role="group" aria-label={t('brandKnowledge.list.filterAria')} sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: K.segBg, borderRadius: '10px', p: '2px', flexWrap: 'wrap', minWidth: 0 }}>
              <SegButton on={filter === SCOPE_FILTER_ALL} onClick={() => setFilter(SCOPE_FILTER_ALL)}>
                {t('brandKnowledge.list.filterAll')} <Count n={items.length} />
              </SegButton>
              <SegButton on={filter === SCOPE_FILTER_GLOBAL} onClick={() => setFilter(SCOPE_FILTER_GLOBAL)}>
                {t('brandKnowledge.list.filterGlobal')} <Count n={counts.global} />
              </SegButton>
              {inlineTargets.map(([id, n]) => (
                <SegButton key={id} on={filter === id} onClick={() => setFilter(id)} dot={targetColorHex(targets.resolve(id)?.color)}>
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130 }}>{targetLabel(id)}</Box>
                  <Count n={n} />
                </SegButton>
              ))}
              {overflowTargets.length > 0 && (
                <>
                  <SegButton on={false} onClick={(e) => setOverflowAnchor(e.currentTarget)}>
                    <Box component="span" sx={{ color: K.primaryDark }}>
                      {t('brandKnowledge.list.filterMore', { count: overflowTargets.length })}
                    </Box>
                  </SegButton>
                  <Menu
                    anchorEl={overflowAnchor}
                    open={!!overflowAnchor}
                    onClose={() => setOverflowAnchor(null)}
                    slotProps={{ paper: { sx: { maxHeight: 340, minWidth: 220, borderRadius: '12px', border: `1px solid ${K.line}` } } }}
                  >
                    {overflowTargets.map(([id, n]) => (
                      <MenuItem
                        key={id}
                        dense
                        selected={filter === id}
                        onClick={() => { setFilter(id); setOverflowAnchor(null); }}
                        sx={{ gap: 1, fontSize: 12.5 }}
                      >
                        <Box component="i" sx={{ width: 7, height: 7, borderRadius: '999px', bgcolor: targetColorHex(targets.resolve(id)?.color), flex: '0 0 7px' }} />
                        <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{targetLabel(id)}</Box>
                        <Count n={n} />
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Box>
          )}

          <Box sx={{ flex: 1 }} />
          {/* The cap is enforced silently; it is only spelled out when an add is refused. */}
          <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: K.ink3 }}>
            {t('brandKnowledge.list.itemCount', { count: items.length })}
          </Box>
        </Box>
      )}

      {error && (
        <Box role="alert" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12, color: K.errorInk }}>
          <ErrorOutlineIcon sx={{ fontSize: 15 }} /> {error}
        </Box>
      )}

      {/* ── Composer ── */}
      {!readOnly && (
        <Box>
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              border: `1px solid ${K.line}`, borderRadius: '10px', bgcolor: K.paper, pl: 1.25, pr: '4px', py: '3px',
              '&:focus-within': { borderColor: K.primary, boxShadow: K.focusRing },
              opacity: atMax ? 0.6 : 1,
            }}
          >
            <AddIcon sx={{ fontSize: 16, color: K.ink3 }} />
            <InputBase
              value={draft}
              disabled={atMax}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onDraftKey}
              onPaste={onDraftPaste}
              placeholder={atMax ? t('brandKnowledge.list.full', { max: limits.maxItems }) : placeholder ?? t('brandKnowledge.list.addPlaceholder')}
              inputProps={{ maxLength: limits.maxCharsPerItem, 'aria-label': placeholder ?? t('brandKnowledge.list.addPlaceholder') }}
              sx={{ flex: 1, fontSize: 13, py: '2px' }}
            />
            {draft && (
              <Box component="span" sx={{ fontSize: 11, color: K.ink3, fontVariantNumeric: 'tabular-nums' }}>
                {draft.length} / {limits.maxCharsPerItem}
              </Box>
            )}
            <ButtonBase
              disabled={atMax || !draft.trim()}
              onClick={() => add(draft)}
              sx={{
                fontSize: 12, fontWeight: 600, px: 1.25, py: '5px', borderRadius: '8px',
                bgcolor: K.primary, color: '#fff', '&:hover': { bgcolor: K.primaryDark },
                '&.Mui-disabled': { bgcolor: K.segBg, color: K.ink3 },
              }}
            >
              {t('brandKnowledge.list.addButton')}
            </ButtonBase>
          </Box>
          <Box sx={{ fontSize: 11, color: K.ink3, mt: 0.5, pl: 0.25, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {filterTarget ? (
              <>
                <Box component="i" sx={{ width: 6, height: 6, borderRadius: '999px', bgcolor: targetColorHex(targets.resolve(filterTarget)?.color) }} />
                {t('brandKnowledge.list.addScopedHint', { target: targetLabel(filterTarget) })}
              </>
            ) : t('brandKnowledge.list.addHint')}
          </Box>
        </Box>
      )}

      {feedbackLine}

      {/* ── List ── */}
      {items.length === 0 ? (
        <Box
          sx={{
            border: `1px dashed ${K.line}`, borderRadius: '12px', px: 2, py: dense ? 1.5 : 2.5,
            fontSize: 12.5, color: K.ink2, textAlign: 'center', bgcolor: K.paper,
          }}
        >
          {emptyHint ?? t('brandKnowledge.list.empty')}
        </Box>
      ) : (
        <Box sx={{ border: `1px solid ${K.line}`, borderRadius: '12px', bgcolor: K.paper, overflow: 'hidden' }}>
          {/* Column head, or the bulk bar when something is selected. */}
          {full && (
            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.25, px: 2, minHeight: 34,
                bgcolor: selection.size ? K.primarySoft : K.headBg, borderBottom: `1px solid ${K.line}`,
              }}
            >
              {selectable && (
                <Check
                  on={allVisibleSelected}
                  mixed={!allVisibleSelected && selectedVisible.length > 0}
                  onClick={toggleAllVisible}
                  label={t('brandKnowledge.list.selectAll')}
                  visible
                />
              )}
              {selection.size > 0 ? (
                <>
                  <Box sx={{ fontSize: 12.5, fontWeight: 600, color: K.primaryDark }}>
                    {t('brandKnowledge.list.selected', { count: selection.size })}
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  {showScope && !scopesUnavailable && (
                    <TargetScopePicker anchor="button" onChange={bulkAssign} targets={targets} />
                  )}
                  <ButtonBase
                    onClick={bulkDelete}
                    sx={{
                      gap: 0.5, fontSize: 12, fontWeight: 600, px: 1.1, py: 0.5, borderRadius: '8px',
                      border: '1px solid rgba(239,68,68,.3)', color: K.errorInk, bgcolor: K.paper,
                      '&:hover': { bgcolor: 'rgba(239,68,68,.06)' },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 15 }} /> {t('brandKnowledge.list.bulkDelete')}
                  </ButtonBase>
                  <ButtonBase onClick={() => setSelection(new Set())} sx={{ fontSize: 12, fontWeight: 600, color: K.ink2, px: 1, py: 0.5, borderRadius: '8px', '&:hover': { color: K.ink } }}>
                    {t('brandKnowledge.list.bulkClear')}
                  </ButtonBase>
                </>
              ) : (
                <>
                  <Box sx={{ ...microLabel, flex: 1 }}>
                    {t('brandKnowledge.list.colItem')}
                    {filtered && (
                      <Box component="span" sx={{ ml: 1, textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
                        {t('brandKnowledge.list.shown', { shown: visible.length, total: items.length })}
                      </Box>
                    )}
                  </Box>
                  {showScope && <Box sx={{ ...microLabel, flex: '0 0 196px' }}>{t('brandKnowledge.list.colScope')}</Box>}
                  {!readOnly && <Box sx={{ flex: '0 0 22px' }} />}
                </>
              )}
            </Box>
          )}

          <Box role="list" sx={{ maxHeight: full ? 460 : 280, overflowY: 'auto', overscrollBehavior: 'contain' }}>
            {visible.length === 0 ? (
              <Box sx={{ px: 2, py: 2.5, textAlign: 'center', fontSize: 12.5, color: K.ink2 }}>
                {t('brandKnowledge.list.noMatch')}{' '}
                <ButtonBase onClick={resetFilters} sx={{ fontSize: 12.5, fontWeight: 600, color: K.primaryDark, verticalAlign: 'baseline', '&:hover': { textDecoration: 'underline' } }}>
                  {t('brandKnowledge.list.resetFilters')}
                </ButtonBase>
              </Box>
            ) : (
              visible.map((text) => (
                <Row
                  key={text}
                  text={text}
                  scopeId={scopes[text]}
                  selected={selection.has(text)}
                  selectable={selectable}
                  selecting={selection.size > 0}
                  readOnly={readOnly}
                  showScope={!!showScope}
                  scopesUnavailable={scopesUnavailable}
                  dense={dense}
                  query={query}
                  editing={editing === text}
                  editValue={editing === text ? editValue : ''}
                  editError={editing === text ? editError : null}
                  maxChars={limits.maxCharsPerItem}
                  targets={targets}
                  actions={actions}
                />
              ))
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default ScopedListManager;
export type { Scopes };
